import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  RefreshCw,
  DollarSign,
  BarChart3,
  Activity,
  Clock,
  AlertCircle,
  Info
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import axios from 'axios';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Stocks = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const fetchStocks = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching stocks from: /api/stocks');
      
      const response = await axios.get('/api/stocks', {
        timeout: 15000 // 15 second timeout
      });
      
      console.log('📊 API Response:', {
        status: response.status,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
        firstItem: Array.isArray(response.data) && response.data.length > 0 ? {
          symbol: response.data[0].symbol,
          name: response.data[0].name,
          price: response.data[0].price
        } : 'No data'
      });
      
      // Only set stocks if we get a valid array with data
      if (Array.isArray(response.data) && response.data.length > 0) {
        setStocks(response.data);
        setLastUpdated(new Date());
        setError(null);
        console.log(`✅ Successfully loaded ${response.data.length} stocks`);
      } else if (Array.isArray(response.data) && response.data.length === 0) {
        console.warn('⚠️ Empty array received from API');
        setStocks([]);
        setError('No stocks found in database');
      } else {
        console.error('❌ Invalid data format received:', response.data);
        setStocks([]);
        setError('Invalid data format received from server');
      }
    } catch (error) {
      console.error('❌ Error fetching stocks:', error);
      
      const errorMessage = error.response 
        ? `Server error: ${error.response.status} - ${error.response.statusText}`
        : error.request 
          ? 'Network error: Could not reach server'
          : `Request error: ${error.message}`;
      
      setError(errorMessage);
      
      // Retry once if this is the first attempt and it's a network error
      if (retryCount === 0 && (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR' || !error.response)) {
        console.log('🔄 Network error detected, retrying in 3 seconds...');
        setTimeout(() => fetchStocks(1), 3000);
        return;
      }
      
      // After retry fails, just show empty array
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, []);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-green-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getChartData = (stock) => {
    const getLastCronTimestamp = () => {
      const now = new Date();
      now.setSeconds(0, 0);
      const minutes = now.getMinutes();
      now.setMinutes(minutes >= 30 ? 30 : 0);
      return now.getTime();
    };

    const now = getLastCronTimestamp();
    const intervalMs = 30 * 60 * 1000; // 30 minutes

    const labels = stock.priceHistory.map((_, i) =>
      new Date(now - (stock.priceHistory.length - 1 - i) * intervalMs)
        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    );

    return {
      labels,
      datasets: [
        {
          label: `${stock.symbol} Price`,
          data: stock.priceHistory,
          borderColor: 'rgb(0, 191, 255)',
          backgroundColor: 'rgba(0, 191, 255, 0.1)',
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 0,
          pointBackgroundColor: 'rgb(0, 191, 255)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 1,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 10, bottom: 10, left: 10, right: 10 }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: { 
          color: '#ffffff',
          font: { size: 11 },
          callback: function(value) {
            return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      x: {
        ticks: {
          color: '#ffffff',
          autoSkip: true,
          maxTicksLimit: 10,
          maxRotation: 45,
          minRotation: 45,
          font: { size: 10 }
        },
        grid: { color: 'rgba(255,255,255,0.05)' }
      }
    },
    plugins: {
      legend: {
        display: true,
        labels: { 
          color: '#ffffff', 
          font: { weight: 'bold' } 
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#1e1e2f',
        titleColor: '#00c9ff',
        bodyColor: '#ffffff',
        borderColor: '#00c9ff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Price: $${context.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-16">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950/30 to-green-950/30"></div>
        
        {/* Retro Grid Background */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px'
        }}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.div
              variants={fadeInUp}
              className="flex justify-center mb-8"
            >
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-green-500 rounded-lg flex items-center justify-center shadow-2xl shadow-blue-500/25 pixel-border">
                  <BarChart3 className="w-12 h-12 text-white" />
                </div>
              </div>
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-green-400 to-blue-500 bg-clip-text text-transparent"
            >
              Voxel Stock Market
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Track real-time stock prices and market trends for your investments
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={fetchStocks}
                disabled={loading}
                className="group relative bg-gradient-to-r from-blue-500 to-green-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-600 hover:to-green-600 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-blue-500/25 flex items-center gap-2 pixel-button disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh Market Data'}
              </button>
              
              {lastUpdated && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                </div>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stocks Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
                <p className="text-xl text-gray-300">Loading market data...</p>
              </div>
            </div>
          ) : stocks.length === 0 ? (
            <div className="text-center py-20">
              <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <p className="text-xl text-gray-300 mb-4">
                {error || "No stock data available"}
              </p>
              {error && (
                <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 max-w-md mx-auto mb-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              <button
                onClick={() => fetchStocks()}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <motion.div 
              initial="initial"
              animate="animate"
              variants={stagger}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {stocks.map((stock, index) => (
                <motion.div
                  key={stock._id || index}
                  variants={fadeInUp}
                  className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        {stock.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-blue-400 font-mono bg-blue-400/10 px-2 py-1 rounded">
                          {stock.symbol}
                        </span>
                        <div className="flex items-center gap-1">
                          {getTrendIcon(stock.trend)}
                          <span className={`text-sm font-semibold ${getTrendColor(stock.trend)}`}>
                            {stock.trend}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center gap-1 mb-1">
                        <DollarSign className="w-4 h-4 text-blue-400" />
                        <span className="text-2xl font-bold text-blue-400">
                          {stock.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Stock Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-purple-400" />
                        <span className="text-gray-300">Volatility:</span>
                      </div>
                      <span className="text-purple-400 font-semibold">
                        {(stock.volatility * 100).toFixed(1)}%
                      </span>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-300 text-sm">Last Event:</span>
                        <p className="text-yellow-400 text-sm">
                          {stock.lastEvent || 'No recent news'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Price Chart */}
                  <div className="h-[180px] mb-4">
                    <Line data={getChartData(stock)} options={chartOptions} />
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Stocks;